// DcInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Button, DialogContent, DialogActions, CircularProgress, Grid, Popover } from '@mui/material';
import { useReactiveVar } from '@apollo/client';
import { AppWord, AppObject, AppStyle, CodeGroup } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import VzmNumberField from '../../common/VzmNumberField';
import VzmRadioForBool from '../../common/VzmRadioForBool';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox, VzmInputBlock } from '../../common/VzmStyled';
import DaumAddressSearch from '../../common/DaumAddressSearch';
import PhoneField from '../../common/PhoneField';
import InputUtil from '../../value/InputUtil';
import { DcInfoDictionary } from './DcInfoDict';
import { codeTreeRepo, dcInfoListRepo } from '../../value/AppModel';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';

const InputMap = DcInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

const MAX_DC_NUM = 999;

function dcCdExists(dcInfoList, dcCd) {
    return dcInfoList.find((dc)=>{return dc.dcCd === dcCd});
}
/**
 * (DC_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-08-26 17:05:21 using DbdbTap
 */
export default function DcInfoInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const dcInfoListData = useReactiveVar(dcInfoListRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const codeTree = useReactiveVar(codeTreeRepo);
    const [anchorAddr, setAnchorAddr] = useState(null);
    const openConfirmDialog = useConfirmDialog();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);
    const pierTimeIntervalCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.PIRE_TIME_INTERVAL_CD, InputMap.pierTimeIntervalCd.id);

    const generateNewDcCd = () => {
        const currentNum = parseInt(input.data.dcCd ? parseInt(input.data.dcCd) : 905);
        let newDcNum = currentNum + 1;
        while(newDcNum <= MAX_DC_NUM) {
            const found = dcCdExists(dcInfoListData.records, ''+newDcNum);
            if(!found) break;
            newDcNum++;
        }
        return ''+newDcNum;
    };

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.data.dcCd = generateNewDcCd();
                inputSet.data.dcTypeCd = ValueMisc.getFirstCode(codeTree, CodeGroup.DC_TYPE_CD);
                inputSet.data.pierTimeIntervalCd = ValueMisc.getFirstCode(codeTree, CodeGroup.PIRE_TIME_INTERVAL_CD);
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    const checkDcExists = (dcCd) => {
        return dcCdExists(dcInfoListData.records, dcCd) ? true : false;
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        if(id === InputMap.dcCd.id) {
            if(!isEdit) {
                const newDcCd = value;
                if(checkDcExists(newDcCd)) {
                    newInput.error.dcCd = true;
                    newInput.hasError = true;
                }
            }
        }
        setInput(newInput);
    };

    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !IsValid.zipCode(addr.zipCd), InputMap, isEdit);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !IsValid.string(addr.addr, 0, 100), InputMap, isEdit);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            title={isEdit ? '출발지 정보 수정' : '출발지 정보 등록'}
        >
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.dcCd} size={AppWord.MEDIUM}
                                value={input.data.dcCd}
                                onChange={onChangeValue}
                                disabled={isEdit}
                                checkError={checkDcExists}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.dcName} size={AppWord.MEDIUM}
                                value={input.data.dcName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmSelector
                                dict={InputMap.dcTypeCd}
                                value={input.data.dcTypeCd}
                                list={dcTypeCdOptions}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>


                    <Grid item xs={2}>
                        <VzmInputBlock>
                            <VzmTextField style={{display:'flex'}}
                                dict={InputMap.zipCd} size={AppWord.MEDIUM}
                                value={input.data.zipCd}
                                onChange={onChangeValue}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={10}>
                        <Box style={{display:'flex', alignItems:'center'}}>
                            <VzmInputBlock style={{flexGrow:1}}>
                                <VzmTextField
                                    dict={InputMap.addr} size={AppWord.MEDIUM}
                                    value={input.data.addr}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </VzmInputBlock>
                            <Button
                                style={{marginLeft:8}} size={AppWord.LARGE}
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                            >
                                주소찾기
                            </Button>
                            <Popover
                                anchorEl={anchorAddr}
                                open={Boolean(anchorAddr)}
                                onClose={()=>{setAnchorAddr(null);}}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                            >
                                <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                            </Popover>
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmSelector
                                dict={InputMap.pierTimeIntervalCd}
                                value={input.data.pierTimeIntervalCd}
                                list={pierTimeIntervalCdOptions}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.docknoStart} size={AppWord.MEDIUM}
                                value={input.data.docknoStart}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.docknoEnd} size={AppWord.MEDIUM}
                                value={input.data.docknoEnd}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>

                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.officeStaffName}
                                value={input.data.officeStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <PhoneField
                                dict={InputMap.officeTelno}
                                value={input.data.officeTelno}
                                onChange={onChangeValue}
                            />
                        </VzmInputBlock>
                    </Grid>

                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.secuStaffName}
                                value={input.data.secuStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <PhoneField
                                dict={InputMap.secuTelno}
                                value={input.data.secuTelno}
                                onChange={onChangeValue}
                            />
                        </VzmInputBlock>
                    </Grid>

                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.transStaffName}
                                value={input.data.transStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={6}>
                        <VzmInputBlock>
                            <VzmTextField
                                dict={InputMap.operStaffName}
                                value={input.data.operStaffName}
                                onChange={onChangeValue}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>

                    <Grid item xs={12}>
                        <VzmInputBlock>
                            <VzmRadioForBool
                                dict={InputMap.useTransType}
                                value={input.data.useTransType}
                                onChange={onChangeValue}
                            />
                        </VzmInputBlock>
                    </Grid>


                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmNumberField
                                dict={InputMap.tempFore}
                                value={input.data.tempFore}
                                onChange={onChangeValue}
                                disabled={input.data.useTransType}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmNumberField
                                dict={InputMap.tempMid}
                                value={input.data.tempMid}
                                onChange={onChangeValue}
                                disabled={input.data.useTransType}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                    <Grid item xs={4}>
                        <VzmInputBlock>
                            <VzmNumberField
                                dict={InputMap.tempRear}
                                value={input.data.tempRear}
                                onChange={onChangeValue}
                                disabled={input.data.useTransType}
                                fullWidth={true}
                            />
                        </VzmInputBlock>
                    </Grid>
                </Grid>
                
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
